import axios from "./Axios";
import { returnErrors } from "./Errors";
import { getCookie } from "./Csrf";
import { getHttpUrl } from "../Utils/config";
import {
  FETCH_CHAT_SESSIONS_SUCCESS,
  FETCH_CHAT_SESSIONS_FAIL,
  FETCH_INDIVIDUAL_CHAT_SESSION_SUCCESS,
  FETCH_INDIVIDUAL_CHAT_SESSION_FAIL,
  CONTINUE_CHAT_SESSION_ADD_SESSION_ID_TO_REDUX,
  MESSAGE_SENT_TO_CHAT_BOT_SUCCESS,
  MESSAGE_SENT_TO_CHAT_BOT_FAIL,
  SET_HAS_RESPONDED,
  NEW_CHAT_SESSION_STARTED_SUCCESS,
  NEW_CHAT_SESSION_STARTED_FAIL,
  RESET_CONTINUE_CHAT_SESSION,
  CHAT_SESSION_IS_NEW_RESET,
  SESSION_DELETE_SUCCESS,
  SESSION_DELETE_FAIL,
  CLEAR_INDIVIDUAL_CHAT_SESSION,
  HAS_CLICKED_ON_SEND_ACTIVE,
  HAS_CLICKED_ON_SEND_INACTIVE,
  ADDED_AI_MODEL,
  ADD_OPTIMISTIC_MESSAGE,
  CUSTOMER_CHATBOT_SUCCESS,
  CUSTOMER_CHATBOT_FAIL,
  FETCH_CUSTOMER_CHATBOT_SUCCESS,
  FETCH_CUSTOMER_CHATBOT_FAIL,
  CUSTOMER_CHATBOT_STREAM_START,
  CUSTOMER_CHATBOT_STREAM_CHUNK,
  CUSTOMER_CHATBOT_STREAM_DONE,
  CUSTOMER_CHATBOT_STREAM_ERROR,
  CHAT_STREAM_START,
  CHAT_STREAM_CHUNK,
  CHAT_STREAM_DONE,
  CHAT_STREAM_ERROR,
} from "./Types";

export const FetchChatSessionsAction = () => (dispatch) => {
  axios
    .get(`/api/ai/sessions/`)
    .then((res) => {
      dispatch({
        type: FETCH_CHAT_SESSIONS_SUCCESS,
        payload: res.data,
      });
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({
        type: FETCH_CHAT_SESSIONS_FAIL,
      });
    });
};

export const FetchIndividualChatSessionAction = (id) => (dispatch) => {
  axios
    .get(`/api/ai/sessions/${id}/messages/`)
    .then((res) => {
      dispatch({
        type: FETCH_INDIVIDUAL_CHAT_SESSION_SUCCESS,
        payload: res.data,
      });
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({
        type: FETCH_INDIVIDUAL_CHAT_SESSION_FAIL,
        payload: err.response && err.response.data,
      });
    });
};

export const ContinueChatSessionAddSessionIDToReduxAction = (id) => {
  return {
    type: CONTINUE_CHAT_SESSION_ADD_SESSION_ID_TO_REDUX,
    payload: id,
  };
};

export const addOptimisticMessage = (message, sessionId) => ({
  type: ADD_OPTIMISTIC_MESSAGE,
  payload: {
    content: message,
    role: "user",
    timestamp: new Date().toISOString(),
    sessionId,
    id: `temp_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
  },
});

export const chatInputAction =
  (message, sessionID, model = "cx2") =>
  (dispatch) => {
    // Add optimistic update for immediate UI feedback
    dispatch(addOptimisticMessage(message, sessionID));

    axios
      .post(`/api/ai/sessions/${sessionID}/messages/`, {
        content: message,
        // model: model || "cx2",
      })
      .then((res) => {
        // Set hasResponded: true — AIInput.componentDidUpdate handles the fetch
        dispatch({
          type: MESSAGE_SENT_TO_CHAT_BOT_SUCCESS,
          payload: res.data,
        });
      })
      .catch((err) => {
        dispatch(returnErrors(err.response?.data, err.response?.status));
        const errMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Something went wrong. Please try again.";
        dispatch({
          type: ADD_OPTIMISTIC_MESSAGE,
          payload: {
            content: errMsg,
            role: "bot",
            timestamp: new Date().toISOString(),
            sessionId: sessionID,
            id: `error_${Date.now()}`,
            isError: true,
          },
        });
        dispatch({ type: MESSAGE_SENT_TO_CHAT_BOT_FAIL });
        dispatch({ type: HAS_CLICKED_ON_SEND_INACTIVE });
      });
  };

export const resetHasResponded = () => ({
  type: SET_HAS_RESPONDED,
  payload: false,
});

export const StartNewChatSessionAction = () => async (dispatch) => {
  try {
    const res = await axios.post(`/api/ai/sessions/`);
    dispatch({
      type: NEW_CHAT_SESSION_STARTED_SUCCESS,
      payload: res.data,
    });
    return res.data; // Return res.data for use in subsequent calls
  } catch (err) {
    dispatch(returnErrors(err.response?.data, err.response?.status));
    dispatch({
      type: NEW_CHAT_SESSION_STARTED_FAIL,
    });
    throw err; // Throw error if you want to handle it later
  }
};

export const resetContinueChatSessionAction = () => ({
  type: RESET_CONTINUE_CHAT_SESSION,
});

export const DeleteChatSessionAction = (id) => (dispatch) => {
  return axios
    .delete(`/api/ai/delete-sessions/${id}/`)
    .then(() => {
      dispatch({ type: SESSION_DELETE_SUCCESS, payload: id });
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({ type: SESSION_DELETE_FAIL });
    });
};

export const RenameChatSessionAction = (id, title) => (dispatch) => {
  return axios
    .patch(`/api/ai/sessions/${id}/rename/`, { title })
    .then((res) => {
      // Session renamed successfully - sessions will be refetched by component
      return res.data;
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      throw err;
    });
};

export const ResetIsNewChatSession = () => ({
  type: CHAT_SESSION_IS_NEW_RESET,
  payload: false,
});

export const ClearIndividualChatSession = () => ({
  type: CLEAR_INDIVIDUAL_CHAT_SESSION,
});

export const hasClicked = () => ({
  type: HAS_CLICKED_ON_SEND_ACTIVE,
});

export const hasNotClicked = () => ({
  type: HAS_CLICKED_ON_SEND_INACTIVE,
});

export const AddAIModelToRedux = (model, modelName) => ({
  type: ADDED_AI_MODEL,
  payload: { model, modelName },
});

export const CustomerChatBotAction = (content) => (dispatch) => {
  axios
    .post(`/api/ai/customer-support/`, { content }, { withCredentials: true })
    .then((res) => {
      // After successful message send, fetch all messages
      return axios.get(`/api/ai/customer-support/`, { withCredentials: true });
    })
    .then((res) => {
      if (res && res.data) {
        dispatch({
          type: CUSTOMER_CHATBOT_SUCCESS,
          payload: res.data,
        });
      }
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({
        type: CUSTOMER_CHATBOT_FAIL,
      });
    });
};

export const FetchCustomerChatBotAction = () => (dispatch) => {
  axios
    .get(`/api/ai/customer-support/`, { withCredentials: true })
    .then((res) => {
      dispatch({
        type: FETCH_CUSTOMER_CHATBOT_SUCCESS,
        payload: res.data,
      });
      return res.data;
    })
    .catch((err) => {
      dispatch(returnErrors(err.response?.data, err.response?.status));
      dispatch({
        type: FETCH_CUSTOMER_CHATBOT_FAIL,
      });
    });
};

export const chatInputStreamAction =
  (message, sessionID) => async (dispatch) => {
    dispatch(addOptimisticMessage(message, sessionID));
    dispatch({ type: HAS_CLICKED_ON_SEND_ACTIVE });
    dispatch({ type: CHAT_STREAM_START });

    const csrfToken = getCookie("csrftoken");
    const baseUrl = getHttpUrl("");
    const accessToken = sessionStorage.getItem("access");

    let response;
    try {
      response = await fetch(
        `${baseUrl}/api/ai/sessions/${sessionID}/messages/stream/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({ content: message }),
        }
      );
    } catch (err) {
      dispatch({ type: CHAT_STREAM_ERROR, payload: err.message });
      dispatch({ type: HAS_CLICKED_ON_SEND_INACTIVE });
      return;
    }

    if (!response.ok) {
      dispatch({
        type: CHAT_STREAM_ERROR,
        payload: `Request failed with status ${response.status}`,
      });
      dispatch({ type: HAS_CLICKED_ON_SEND_INACTIVE });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;
          try {
            const data = JSON.parse(dataStr);
            if (data.delta !== undefined) {
              dispatch({ type: CHAT_STREAM_CHUNK, payload: data.delta });
              // Yield to the event loop after each token so React flushes a
              // render per chunk — without this, React 18 batches all synchronous
              // dispatches from a single reader.read() call into one render,
              // making the text appear all at once instead of word by word.
              await new Promise((r) => setTimeout(r, 0));
            } else if (data.done) {
              dispatch({
                type: CHAT_STREAM_DONE,
                payload: {
                  sessionId: data.session_id,
                  sessionTitle: data.session_title,
                },
              });
            } else if (data.error) {
              dispatch({ type: CHAT_STREAM_ERROR, payload: data.error });
            }
          } catch {}
        }
      }
    } catch (err) {
      dispatch({ type: CHAT_STREAM_ERROR, payload: err.message });
    } finally {
      dispatch({ type: HAS_CLICKED_ON_SEND_INACTIVE });
    }
  };

export const CustomerChatBotStreamAction = (content) => async (dispatch) => {
  dispatch({ type: CUSTOMER_CHATBOT_STREAM_START, payload: { content } });

  const csrfToken = getCookie("csrftoken");
  const baseUrl = getHttpUrl("");

  let response;
  try {
    response = await fetch(`${baseUrl}/api/ai/customer-support/stream/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: JSON.stringify({ content }),
    });
  } catch (err) {
    dispatch({ type: CUSTOMER_CHATBOT_STREAM_ERROR, payload: err.message });
    return;
  }

  if (!response.ok) {
    dispatch({
      type: CUSTOMER_CHATBOT_STREAM_ERROR,
      payload: `Request failed with status ${response.status}`,
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const dataStr = line.slice(6).trim();
        if (!dataStr) continue;
        try {
          const data = JSON.parse(dataStr);
          if (data.delta !== undefined) {
            dispatch({ type: CUSTOMER_CHATBOT_STREAM_CHUNK, payload: data.delta });
            await new Promise((r) => setTimeout(r, 0));
          } else if (data.done) {
            dispatch({ type: CUSTOMER_CHATBOT_STREAM_DONE });
          } else if (data.error) {
            dispatch({ type: CUSTOMER_CHATBOT_STREAM_ERROR, payload: data.error });
          }
        } catch {}
      }
    }
  } catch (err) {
    dispatch({ type: CUSTOMER_CHATBOT_STREAM_ERROR, payload: err.message });
  }
};
